import { Component, OnInit, HostBinding, ViewChild, Renderer2, ElementRef, AfterViewInit, OnDestroy, Injector } from '@angular/core';
import { SharedService } from '../services/shared.service';
import { HomeService } from '../services/home.service';
import { Constants } from '../constants';
import { ModuleNameEnum, Piwiktrack } from '../shared/piwiktrack';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { mvnfbuAnimations } from '../shared/animations';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BackgroundLocalComponent } from '../shared/background-local/background-local.component';
import { LocalNewsModel } from '../typings';
import { takeUntil } from 'rxjs/operators';
import { ActivityLoggerService } from '../services/activityLogger.service';
import { VideoStallHandlerService } from '../services/video-stall-handler.service';
import { AppComponent } from '../app.component';
import { ImageUtilsService } from '../services/image-utils.service';
import { LocalNewsBaseComponent } from '../shared/local-news-base.component';

@Component({
  selector: 'app-big-local',
  templateUrl: './big-local.component.html',
  styleUrls: ['./big-local.component.scss'],
  standalone: false,
  animations: [
    ...mvnfbuAnimations,
    trigger('newsCardAnimation', [
      state('up', style({
        transform: 'translateY(0)'
      })),
      state('down', style({
        transform: 'translateY(30.6vh)'
      })),
      transition('void => up', [
        style({ transform: 'translateY(30.6vh)' }),
        animate('1s ease-out', style({ transform: 'translateY(0)' }))
      ]),
      transition('down => up', [
        animate('1s ease-out')
      ]),
      transition('up => down', [
        animate('1s ease-in')
      ])
    ]),
    trigger('imageContainerSlide', [
      state('static', style({
        transform: 'translateX(0)'
      })),
      state('moved', style({
        transform: 'translateX(-100vw)'
      })),
      transition('static => moved', [
        animate('{{ duration }}ms {{ delay }}ms {{ easing }}')
      ], { params: { duration: 1000, delay: 8000, easing: 'ease-out' } })
    ])
  ]
})
export class BigLocalComponent extends LocalNewsBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'fullWith';
  
  @ViewChild(BackgroundLocalComponent) background: BackgroundLocalComponent;

  public animationUrl: string = '';
  public moveToTheLeft: boolean = false;
  public safetyIconUrl: string = '';
  public showNewsCardText: boolean = false;
  public animTabs: boolean = false;
  public zoomIn: boolean = false;
  public animationKey: number = 0;
  public imageContainerState: 'static' | 'moved' = 'static';
  public imageContainerAnimationParams = { 
    duration: 1000, 
    delay: 8000, 
    easing: 'ease-out' 
  };
  public animOut: boolean = true;

  // Portrait mode detection
  public isFirstImagePortrait: boolean = false;
  public isSecondImagePortrait: boolean = false;
  public staticFirstImageUrl: string = '';

  private newsDisplayTimeoutId?: number;
  private appComponent: AppComponent;
  private animationEndId?: number;
  private safetyIconTimeoutId?: number;

  // ==================== LIFECYCLE HOOKS ====================
  
  constructor(sharedService: SharedService, homeService: HomeService, private router: Router, private el: ElementRef, private renderer: Renderer2, logger: ActivityLoggerService, private injector: Injector, private authService: AuthService, videoStallHandlerService: VideoStallHandlerService, private imageUtils: ImageUtilsService) {
    super(sharedService, homeService, logger, videoStallHandlerService);
    this.blobUrl = sharedService.blobUrl;
    this.appComponent = this.injector.get(AppComponent);
  }

  ngOnInit() {
    this.initializeData();
  }

  ngAfterViewInit(){
    this.initializeBackground();
  }

  override ngOnDestroy(): void {
    console.log("ngOnDestroy LocalComponent");
    super.ngOnDestroy();
    this.clearAllTimeouts();
  }

  // ==================== MÃ‰THODES D'INITIALISATION ====================

  public initializeData() {
    this.homeService.getNews()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (news) => {
        console.log("Initializing local news data");
        if(news == null) {
          this.homeService.loadNews();
        }
        if (news && news.localNews && news.localNews.length > 0) {  
          this.localNews = news.localNews;
          this.currentNews = this.localNews[this.currentNewsIndex];
          this.futureNews = this.localNews[this.currentNewsIndex];
          this.initializeBackground();
          this.showNewsCardText = true;
          this.animationKey++;
          this.zoomIn = true;
          this.animTabs = true;
          this.setTimerToDisplayChange();
          this.updateCurrentNewsImages(true);
        }
        else if(news && (news.internalNews && news.internalNews.length > 0)) {
          console.warn("No local news available, redirecting to home.");
          this.sharedService.navigateByDisplayMode('home');
        }
        this.withOtherNews = (news?.internalNews && news.internalNews.length > 0) ?? false;
      }
      ,
        error: (e: any) => {
          console.error(e);
          this.sharedService.navigate([Constants.RedirectUrl.Error]);
        }
      });
    this.sharedService.getToken().then(token => {
      Piwiktrack.piwikTrackPage(this.homeService.uuid, ModuleNameEnum.ScreenView, token);
    });
  }

  private initializeBackground(): void {
    if(this.background)
    {
        const isSafety = this.currentNews?.tag === 'safety';
        this.background.setInitialValue(isSafety ? '' : this.getFirstImage(this.currentNews), this.currentNews?.background?.image);
    }
  }

  private updateCurrentNewsImages(isInitial: boolean): void {
    const firstImage = this.getFirstImage(this.currentNews);
    this.checkImageRatio(firstImage, false);
    this.imageUtils.extractStaticBackground(firstImage).then(url => {
      this.staticFirstImageUrl = url;
      const isSafety = this.currentNews?.tag === 'safety';
      if (isInitial) {
        if (this.background) { this.background.setInitialValue(isSafety ? '' : url, this.currentNews?.background?.image); }
      } else {
        this.background.changeValue(isSafety ? '' : url, this.currentNews?.background?.image);
      }
    });
    if (this.hasSecondImage()) {
      this.checkImageRatio(this.currentNews!.images[1], true);
    }
  }

  // ==================== NAVIGATION ENTRE LES NEWS ====================

  protected moveToNextNews(): void {
    console.log("moveToNextNews");
    this.imageContainerState = 'static';
    this.zoomIn = false;
    this.clearVideoTimeout();
    this.clearMetadataTimeout();
    this.clearSafetyIconTimeout();
    this.videoReady = false;
    this.videoIsPlaying = false;
    this.safetyIconUrl = '';
    this.isTransitionSafetyToSafety = this.currentNews?.tag === 'safety' && this.isTheNextNewsSafety();
    this.currentNewsIndex = (this.count + 1) % this.localNews.length;
    this.count++;
    if((this.count === 5 || this.count >= this.localNews.length)){ 
      if(this.withOtherNews){
        console.log("Final animation to other news");
        this.showNewsCardText = false;
        this.animationKey++;
        this.futureNews = undefined;
        this.sharedService.navigateByDisplayMode('home');
      }
      else{
        this.count = 0;
        this.currentNewsIndex = 0;
        this.futureNews = this.localNews[this.currentNewsIndex];
        this.showNewsCardText = false;
        this.animationKey++;
        this.homeService.loadNews();
        this.sharedService.navigateByDisplayMode('local');
      }
    }
    else{
      this.futureNews = this.localNews[this.currentNewsIndex];
      this.showNewsCardText = false;
      this.zoomIn = false;
      this.animTabs = false;
      this.currentNews = this.futureNews;
        
        setTimeout(() => {
          this.showNewsCardText = true;
          this.animationKey++;
          this.zoomIn = true;
          this.animTabs = true;
          this.updateCurrentNewsImages(false);
        }, 50);
        
        this.setTimerToDisplayChange();
    }
  }

  // ==================== UTILITAIRES ====================

  public hasSecondImage(): boolean {
    return (this.currentNews?.images && this.currentNews.images.length > 1) || false;
  }

  public getFirstImage(news: LocalNewsModel | undefined): string {
    if (!news) return '';
    if (news.image) return news.image;
    if (news.images && news.images.length > 0) return news.images[0];
    return '';
  }

  getSafetyTypeIcon(arg0: string|null) {
    return this.safetyIconUrl;
  }

  // ==================== GESTION DES ANIMATIONS ====================
  
  private setTimerToDisplayChange(): void {
    const timing = 4000;
    let displayDuration = this.getNewsDisplayDuration();
    const animationDelay = Math.max(displayDuration - timing - 1000, 2000);
    if(this.currentNews?.subType){
      displayDuration += 6000;
    }
    this.imageContainerAnimationParams = {
      duration: 1000,
      delay: animationDelay,
      easing: 'ease-out'
    };
    
    this.clearDisplayChangeTimeout();
    this.newsDisplayTimeoutId = window.setTimeout(() => {
      console.log("Timer ended - changing news display");
      this.showNewsCardText = false;
      this.animationKey++;
      if(this.currentNews?.video){
         this.displayStreamVideo();
      }
      else{
        this.moveToTheLeft = true;
        this.imageContainerState = 'moved';
        this.loadAnimation();
        this.loadSafetyIcon(animationDelay);
        this.animOut = false;
      }
    }, (timing));

    if(!this.currentNews?.video){
      this.animationEndId = window.setTimeout(() => {
        this.moveToTheLeft = false;
        this.moveToNextNews();      
      }, displayDuration);
    }
  }

  private loadAnimation(): void {
    if(this.currentNews && this.currentNews.withAnimation) {
      const lang = this.appComponent.currentLanguage || 'fr';
      console.log(this.currentNews.tag);
      let imageName = Constants.Animations[this.currentNews.tag as keyof typeof Constants.Animations] || this.currentNews.tag;
      imageName = imageName + '-' + lang.toUpperCase() + Constants.Animations.Extension;
      this.animationUrl = this.sharedService.buildUrlBlob(imageName, 'Local-news/animation/');
      console.log("animation url: " + this.animationUrl);
    }
  }

  // ==================== DETECTION DU RATIO D'IMAGE ====================
  
  public checkImageRatio(imageUrl: string, isSecondImage: boolean = false): void {
    if (!imageUrl) return;
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      this.setImagePortrait(isSecondImage, aspectRatio <= 1.35);
    };
    img.onerror = () => {
      this.setImagePortrait(isSecondImage, false);
    };
    img.src = imageUrl;
  }

  private setImagePortrait(isSecondImage: boolean, value: boolean): void {
    if (isSecondImage) {
      this.isSecondImagePortrait = value;
    } else {
      this.isFirstImagePortrait = value;
    }
  }

  // ==================== CLEANUP METHODS ====================

  private clearAllTimeouts(): void {
    this.clearDisplayChangeTimeout();
    this.clearVideoTimeout();
    this.clearMetadataTimeout();
    this.clearSafetyIconTimeout();
  }

  private clearDisplayChangeTimeout(): void {
    this.newsDisplayTimeoutId = this.clearTimer(this.newsDisplayTimeoutId);
  }

  private clearSafetyIconTimeout(): void {
    this.safetyIconTimeoutId = this.clearTimer(this.safetyIconTimeoutId);
  }

  private loadSafetyIcon(delay: number): void {
    if (this.currentNews?.subType) {
      this.safetyIconTimeoutId = window.setTimeout(() => {
        let imageName = Constants.Animations[this.currentNews!.subType as keyof typeof Constants.Animations] || this.currentNews!.subType;
        imageName = imageName + Constants.Animations.Extension;
        this.safetyIconUrl = this.sharedService.buildUrlBlob(imageName, 'Local-news/animation/safety/');
      }, delay + 1000);
    }
  }

  protected override clearVideoTimeout(): void {
    this.animationEndId = this.clearTimer(this.animationEndId);
    super.clearVideoTimeout();
  }
}
